/** Concrete example for a Venn operation. */
export interface VennOperationExample {
  /** LaTeX strings defining each set, e.g. `A = \{1,2,3,4\}`. */
  readonly setDefinitions: string[];
  /** LaTeX string showing the result, e.g. `A \cap B = \{2,3\}`. */
  readonly resultLatex: string;
}

/** Definition of a 2-set Venn diagram operation used by the dashboard. */
export interface VennOperationDef {
  /** Builds the LaTeX notation string given set names [a, b]. */
  readonly buildNotation: (a: string, b: string) => string;
  /** Builds a human-readable description given set names [a, b]. */
  readonly buildDescription: (a: string, b: string) => string;
  /** Builds a plain-English explanation given set names [a, b]. */
  readonly buildExplanation: (a: string, b: string) => string;
  /** Builds a concrete example using fixed number sets given set names [a, b]. */
  readonly buildExample: (a: string, b: string) => VennOperationExample;
  /** Regions to highlight as [inA, inB] boolean tuples (set-name-independent). */
  readonly highlighted: ReadonlyArray<[boolean, boolean]>;
}

/**
 * All examples use consistent sets so students can compare across operations:
 *   A = {1,2,3,4},  B = {3,4,5,6},  U = {1,2,3,4,5,6,7,8}
 *
 * Region breakdown:
 *   Only A: {1,2}   Only B: {5,6}   Both: {3,4}   Neither: {7,8}
 */
const exampleSets = (a: string, b: string): string[] => [`${a} = \\{1, 2, 3, 4\\}`, `${b} = \\{3, 4, 5, 6\\}`];

export const VENN_OPERATIONS: readonly VennOperationDef[] = [
  {
    buildNotation: (a, b) => `${a} \\cap ${b}`,
    buildDescription: () => 'Intersection',
    buildExplanation: (a, b) =>
      `The intersection of two sets contains only the elements that are common to both sets. Think of it as the overlap \u2014 what ${a} and ${b} share. In probability, P(${a} \u2229 ${b}) represents the probability that both events ${a} and ${b} occur simultaneously.`,
    buildExample: (a, b) => ({
      setDefinitions: exampleSets(a, b),
      resultLatex: `${a} \\cap ${b} = \\{3, 4\\}`,
    }),
    highlighted: [[true, true]],
  },
  {
    buildNotation: (a, b) => `${a} \\cup ${b}`,
    buildDescription: () => 'Union',
    buildExplanation: (a, b) =>
      `The union of two sets contains every element that appears in at least one of the sets. It combines ${a} and ${b} together, without repeating shared elements. In probability, P(${a} \u222A ${b}) is the probability that at least one of the two events occurs.`,
    buildExample: (a, b) => ({
      setDefinitions: exampleSets(a, b),
      resultLatex: `${a} \\cup ${b} = \\{1, 2, 3, 4, 5, 6\\}`,
    }),
    highlighted: [
      [true, false],
      [false, true],
      [true, true],
    ],
  },
  {
    buildNotation: (a) => `${a}'`,
    buildDescription: (a) => `Complement of ${a}`,
    buildExplanation: (a) =>
      `The complement of ${a} contains everything in the universal set that is not in ${a}. If ${a} describes an event, then ${a}\u2032 is the event that ${a} does not happen. Note that P(${a}) + P(${a}\u2032) = 1.`,
    buildExample: (a, b) => ({
      setDefinitions: [...exampleSets(a, b), `U = \\{1, 2, 3, 4, 5, 6, 7, 8\\}`],
      resultLatex: `${a}' = \\{5, 6, 7, 8\\}`,
    }),
    highlighted: [
      [false, true],
      [false, false],
    ],
  },
  {
    buildNotation: (a, b) => `${a} \\cap ${b}'`,
    buildDescription: (a, b) => `${a} but not ${b}`,
    buildExplanation: (a, b) =>
      `This is the set difference ${a} \u2216 ${b}: elements that belong to ${a} but are excluded from ${b}. In a Venn diagram it is the part of ${a} that does not overlap with ${b}. In probability, it represents event ${a} occurring while ${b} does not.`,
    buildExample: (a, b) => ({
      setDefinitions: exampleSets(a, b),
      resultLatex: `${a} \\cap ${b}' = \\{1, 2\\}`,
    }),
    highlighted: [[true, false]],
  },
  {
    buildNotation: (a, b) => `${a}' \\cap ${b}`,
    buildDescription: (a, b) => `${b} but not ${a}`,
    buildExplanation: (a, b) =>
      `The mirror of the previous operation: elements that belong to ${b} but are not in ${a}. This is the part of ${b} outside the overlap. In probability, event ${b} occurs while ${a} does not.`,
    buildExample: (a, b) => ({
      setDefinitions: exampleSets(a, b),
      resultLatex: `${a}' \\cap ${b} = \\{5, 6\\}`,
    }),
    highlighted: [[false, true]],
  },
  {
    buildNotation: (a, b) => `${a} \\cup ${b}'`,
    buildDescription: (a, b) => `${a} or not ${b}`,
    buildExplanation: (a, b) =>
      `This set includes everything that is in ${a}, plus everything outside ${b}. The only elements excluded are those in ${b} alone (without also being in ${a}). It covers most of the universal set.`,
    buildExample: (a, b) => ({
      setDefinitions: [...exampleSets(a, b), `U = \\{1, 2, 3, 4, 5, 6, 7, 8\\}`],
      resultLatex: `${a} \\cup ${b}' = \\{1, 2, 3, 4, 7, 8\\}`,
    }),
    highlighted: [
      [true, false],
      [true, true],
      [false, false],
    ],
  },
  {
    buildNotation: (a, b) => `${a}' \\cup ${b}`,
    buildDescription: (a, b) => `Not ${a} or ${b}`,
    buildExplanation: (a, b) =>
      `This set includes everything outside ${a}, plus everything in ${b}. The only elements excluded are those in ${a} alone (without also being in ${b}). It is the mirror of ${a} \u222A ${b}\u2032.`,
    buildExample: (a, b) => ({
      setDefinitions: [...exampleSets(a, b), `U = \\{1, 2, 3, 4, 5, 6, 7, 8\\}`],
      resultLatex: `${a}' \\cup ${b} = \\{3, 4, 5, 6, 7, 8\\}`,
    }),
    highlighted: [
      [false, true],
      [true, true],
      [false, false],
    ],
  },
  {
    buildNotation: (a, b) => `(${a} \\cap ${b})'`,
    buildDescription: () => "De Morgan's (Union)",
    buildExplanation: (a, b) =>
      `The complement of the intersection: everything except the elements common to both ${a} and ${b}. By De Morgan\u2019s law this equals ${a}\u2032 \u222A ${b}\u2032. It is useful for rewriting complex probability expressions into simpler unions.`,
    buildExample: (a, b) => ({
      setDefinitions: [...exampleSets(a, b), `U = \\{1, 2, 3, 4, 5, 6, 7, 8\\}`],
      resultLatex: `(${a} \\cap ${b})' = \\{1, 2, 5, 6, 7, 8\\}`,
    }),
    highlighted: [
      [true, false],
      [false, true],
      [false, false],
    ],
  },
  {
    buildNotation: (a, b) => `(${a} \\cup ${b})'`,
    buildDescription: () => "De Morgan's (Intersection)",
    buildExplanation: (a, b) =>
      `The complement of the union: elements that belong to neither ${a} nor ${b}. By De Morgan\u2019s law this equals ${a}\u2032 \u2229 ${b}\u2032. In probability, it is the chance that neither event occurs.`,
    buildExample: (a, b) => ({
      setDefinitions: [...exampleSets(a, b), `U = \\{1, 2, 3, 4, 5, 6, 7, 8\\}`],
      resultLatex: `(${a} \\cup ${b})' = \\{7, 8\\}`,
    }),
    highlighted: [[false, false]],
  },
];
